import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate, type Location } from 'react-router';
import { useAuth } from './use-auth';
import { ApiError } from '@/lib/api-client';

interface LocationState {
  from?: Location;
}

/**
 * Login screen. Plain controlled form (react-hook-form is overkill for
 * 2 fields with no nested validation). Surfaces backend AppException
 * codes via a friendly French message map.
 */
export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If already authenticated, bounce away from /login.
  if (status === 'authenticated') {
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(messageForError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page px-4">
      <div className="w-full max-w-sm space-y-8">
        <div className="space-y-2 text-center">
          <div className="font-display text-4xl font-bold tracking-tight text-brand-primary">
            IOA
          </div>
          <p className="text-sm text-ink-500">Connexion à votre espace</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-5 rounded-md bg-surface-card p-8 shadow-card"
          noValidate
        >
          <Field label="Adresse email" htmlFor="email">
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-sm border border-border-strong bg-white px-3 py-2.5 text-ink-800 outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-pale"
            />
          </Field>

          <Field label="Mot de passe" htmlFor="password">
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-sm border border-border-strong bg-white px-3 py-2.5 text-ink-800 outline-none transition focus:border-brand-primary focus:ring-2 focus:ring-brand-pale"
            />
          </Field>

          {error && (
            <div role="alert" className="rounded-sm bg-peach-100 px-3 py-2 text-sm text-peach-500">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || email.length === 0 || password.length === 0}
            className="w-full rounded-sm bg-brand-primary px-4 py-2.5 font-medium text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink-800">
        {label}
      </label>
      {children}
    </div>
  );
}

const FRIENDLY_MESSAGES: Record<string, string> = {
  AUTH_INVALID_CREDENTIALS: 'Email ou mot de passe incorrect.',
  AUTH_USER_SUSPENDED: 'Compte suspendu — contactez votre administrateur.',
  AUTH_USER_INACTIVE: 'Compte désactivé.',
};

function messageForError(err: unknown): string {
  if (err instanceof ApiError) {
    const friendly = err.code ? FRIENDLY_MESSAGES[err.code] : undefined;
    if (friendly) return friendly;
    if (err.status === 401) {
      return FRIENDLY_MESSAGES.AUTH_INVALID_CREDENTIALS ?? 'Identifiants invalides.';
    }
    if (err.status >= 500) return 'Erreur serveur — réessayez dans un instant.';
    return err.message;
  }
  return 'Impossible de joindre le serveur. Vérifiez votre connexion.';
}
